import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { pushDetailNavState } from '../utils/detailNavState';
import Icon from '../components/Icon';
import PromoConfirmModal from '../components/PromoConfirmModal';
import CustomDatePicker from '../components/CustomDatePicker';
import { createPromoCode, getPromoCodes } from '../api';
import {
  PROMO_CODE_TYPE,
  filterPromoCodesByQuery,
  formatDisplayDate,
} from '../utils/promoCodeUtils';

const DEFAULT_CREATE_FORM = {
  code: '',
  startsAt: '',
  expiresAt: '',
};

const AdminPromoCodes = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = i18n.language || 'ru';

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);
  const [createConfirmError, setCreateConfirmError] = useState(null);

  // Стабильная ссылка на актуальный t, чтобы loadList не пересоздавался
  // (иначе смена t → новый loadList → лишний запрос списка).
  const tRef = useRef(t);
  tRef.current = t;

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await getPromoCodes();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setListError(e?.message || tRef.current('admin.promoCodes.loadError'));
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Начальная загрузка ровно один раз. Гард через ref гасит и повторный
  // вызов из-за двойного маунта React.StrictMode в dev-режиме.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadList();
  }, [loadList]);

  const filteredList = useMemo(
    () => filterPromoCodesByQuery(list, searchQuery),
    [list, searchQuery]
  );

  const handleOpenPromo = (id) => {
    navigate(`/admin/promo-codes/${id}`, { state: pushDetailNavState(location) });
  };

  const buildCreatePayload = () => {
    const code = createForm.code.trim();
    if (!code) {
      setCreateError(t('admin.promoCodes.errorCode'));
      return null;
    }

    const payload = {
      code,
      type: PROMO_CODE_TYPE,
      bonusAmount: 0,
    };
    const startsAt = createForm.startsAt || null;
    const expiresAt = createForm.expiresAt || null;
    if (startsAt) payload.startsAt = startsAt;
    if (expiresAt) payload.expiresAt = expiresAt;
    return payload;
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(false);
    setCreateConfirmError(null);

    const payload = buildCreatePayload();
    if (!payload) return;

    setPendingCreatePayload(payload);
    setCreateConfirmOpen(true);
  };

  const handleCloseCreateConfirm = () => {
    if (createSubmitting) return;
    setCreateConfirmOpen(false);
    setPendingCreatePayload(null);
    setCreateConfirmError(null);
  };

  const handleConfirmCreate = async () => {
    if (!pendingCreatePayload) return;

    setCreateSubmitting(true);
    setCreateConfirmError(null);
    try {
      const created = await createPromoCode(pendingCreatePayload);
      setCreateSuccess(true);
      setCreateForm(DEFAULT_CREATE_FORM);
      setCreateOpen(false);
      setCreateConfirmOpen(false);
      setPendingCreatePayload(null);
      await loadList();
      if (created?.id) {
        navigate(`/admin/promo-codes/${created.id}`, { state: pushDetailNavState(location) });
      }
      window.setTimeout(() => setCreateSuccess(false), 5000);
    } catch (err) {
      setCreateConfirmError(err?.message || t('admin.promoCodes.createError'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const createConfirmItems = pendingCreatePayload
    ? [
        { label: t('admin.promoCodes.code'), value: pendingCreatePayload.code },
        {
          label: t('admin.promoCodes.type'),
          value: t(`admin.promoCodes.types.${pendingCreatePayload.type}`, pendingCreatePayload.type),
        },
        {
          label: t('admin.promoCodes.startsAt'),
          value: pendingCreatePayload.startsAt
            ? formatDisplayDate(pendingCreatePayload.startsAt, locale)
            : t('admin.promoCodes.confirm.notSpecified'),
        },
        {
          label: t('admin.promoCodes.expiresAt'),
          value: pendingCreatePayload.expiresAt
            ? formatDisplayDate(pendingCreatePayload.expiresAt, locale)
            : t('admin.promoCodes.confirm.notSpecified'),
        },
      ]
    : [];

  const searchActive = searchQuery.trim().length > 0;

  return (
    <div className="admin-promo-codes admin-promo-list-page">
      <div className="admin-promo-list-shell">
        <header className="admin-promo-list-header">
          <div className="admin-promo-list-header-text">
            <h3 className="admin-promo-list-title">
              <span className="admin-promo-list-title-icon">
                <Icon name="tag" size={18} />
              </span>
              {t('admin.promoCodes.title')}
            </h3>
            <p className="admin-promo-list-subtitle">{t('admin.promoCodes.listSubtitle')}</p>
          </div>
          <div className="admin-promo-header-actions">
            <button
              type="button"
              className="btn-premium-action-new admin-promo-refresh-btn"
              onClick={loadList}
              disabled={listLoading}
              title={t('admin.promoCodes.refresh')}
            >
              <Icon name="refresh" size={16} />
            </button>
            <button
              type="button"
              className="btn-premium-action-new"
              onClick={() => {
                setCreateOpen((v) => !v);
                setCreateError(null);
              }}
            >
              <Icon name={createOpen ? 'x' : 'plus'} size={16} />
              <span>{createOpen ? t('admin.promoCodes.cancelCreate') : t('admin.promoCodes.create')}</span>
            </button>
          </div>
        </header>

        {createSuccess && (
          <div className="form-status success admin-promo-banner" role="status">
            {t('admin.promoCodes.createSuccess')}
          </div>
        )}

        <div
          className={`admin-promo-create-collapse${createOpen ? ' is-open' : ''}`}
          aria-hidden={!createOpen}
        >
          <div className="admin-promo-create-collapse-inner">
            <form className="admin-promo-create-panel" onSubmit={handleCreateSubmit}>
              <div className="admin-promo-create-panel-inner">
                <h4 className="admin-promo-create-panel-title">{t('admin.promoCodes.create')}</h4>
                <div className="admin-promo-create-grid">
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">{t('admin.promoCodes.code')}</span>
                    <input
                      className="premium-input-minimal"
                      value={createForm.code}
                      onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value }))}
                      placeholder={t('admin.promoCodes.codePlaceholder')}
                      required
                    />
                  </label>
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.promoCodes.startsAt')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.startsAt ? new Date(createForm.startsAt) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, startsAt: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.promoCodes.expiresAt')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.expiresAt ? new Date(createForm.expiresAt) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, expiresAt: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                </div>
                {createError && (
                  <div className="form-status error" role="alert">
                    {createError}
                  </div>
                )}
                <div className="admin-promo-create-actions">
                  <button type="submit" className="btn-premium-action-new" disabled={createSubmitting}>
                    <Icon name="check" size={16} />
                    <span>{createSubmitting ? t('admin.promoCodes.submitting') : t('admin.promoCodes.submit')}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-promo-toolbar">
          <div className="admin-promo-search-wrap">
            <Icon name="search" size={18} className="admin-promo-search-icon" />
            <input
              type="search"
              className="admin-promo-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.promoCodes.searchPlaceholder')}
              aria-label={t('admin.promoCodes.searchPlaceholder')}
            />
            {searchQuery && (
              <button
                type="button"
                className="admin-promo-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label={t('admin.promoCodes.clearSearch')}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
          <span className="admin-promo-results-count">
            {searchActive
              ? t('admin.promoCodes.resultsCount', { count: filteredList.length, total: list.length })
              : t('admin.promoCodes.totalCount', { count: list.length })}
          </span>
        </div>

        <div className="admin-promo-list-body">
          {listLoading && (
            <p className="admin-promo-muted admin-promo-list-empty">{t('common.loading')}</p>
          )}
          {listError && !listLoading && (
            <div className="form-status error" role="alert">
              {listError}
            </div>
          )}
          {!listLoading && !listError && list.length === 0 && (
            <div className="admin-promo-empty-state">
              <Icon name="tag" size={40} />
              <p>{t('admin.promoCodes.empty')}</p>
              <button type="button" className="btn-premium-action-new" onClick={() => setCreateOpen(true)}>
                <Icon name="plus" size={16} />
                <span>{t('admin.promoCodes.create')}</span>
              </button>
            </div>
          )}
          {!listLoading && list.length > 0 && filteredList.length === 0 && (
            <div className="admin-promo-empty-state">
              <Icon name="search" size={40} />
              <p>{t('admin.promoCodes.searchEmpty')}</p>
            </div>
          )}
          {!listLoading && filteredList.length > 0 && (
            <div className="admin-promo-table-wrap admin-promo-table-wrap--list">
              <table className="admin-promo-table admin-promo-table--list">
                <thead>
                  <tr>
                    <th>{t('admin.promoCodes.code')}</th>
                    <th>{t('admin.promoCodes.type')}</th>
                    <th>{t('admin.promoCodes.active')}</th>
                    <th>{t('admin.promoCodes.startsAt')}</th>
                    <th>{t('admin.promoCodes.expiresAt')}</th>
                    <th className="admin-promo-table-col-action" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((item) => (
                    <tr
                      key={item.id}
                      className="admin-promo-table-row-link"
                      onClick={() => handleOpenPromo(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenPromo(item.id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                    >
                      <td>
                        <code className="admin-promo-code">{item.code}</code>
                      </td>
                      <td>{t(`admin.promoCodes.types.${item.type}`, item.type)}</td>
                      <td>
                        <span className={`admin-promo-badge ${item.active ? 'active' : 'inactive'}`}>
                          {item.active ? t('admin.promoCodes.activeYes') : t('admin.promoCodes.activeNo')}
                        </span>
                      </td>
                      <td className="admin-promo-table-date">{formatDisplayDate(item.startsAt, locale)}</td>
                      <td className="admin-promo-table-date">{formatDisplayDate(item.expiresAt, locale)}</td>
                      <td className="admin-promo-table-col-action">
                        <Icon name="chevron-right" size={18} className="admin-promo-row-chevron" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PromoConfirmModal
        open={createConfirmOpen}
        title={t('admin.promoCodes.confirm.createTitle')}
        subtitle={t('admin.promoCodes.confirm.createSubtitle')}
        items={createConfirmItems}
        error={createConfirmError}
        submitting={createSubmitting}
        onCancel={handleCloseCreateConfirm}
        onConfirm={handleConfirmCreate}
        cancelLabel={t('admin.promoCodes.confirm.cancel')}
        confirmLabel={createSubmitting ? t('admin.promoCodes.submitting') : t('admin.promoCodes.confirm.confirm')}
        titleId="promo-create-confirm-title"
      />
    </div>
  );
};

export default AdminPromoCodes;
